import React, { useCallback, useState } from 'react';

import {
  UseSearchOrderItemsParams,
  UseSearchOrdersParams,
  UseSearchPartsParams,
  UseSearchQuoteItemsParams,
  UseSearchQuotesParams,
} from '@frontend/api';
import {
  Button,
  Box,
  CheckboxGroup,
  Checkbox,
  FormControl,
  FormLabel,
  UserGroupBadge,
} from '@frontend/components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { useFeatureFlagValue } from 'store/session/hooks';
import { getUserGroupCollectionId } from 'store/session/selectors';

import { useVisibleUserGroups } from 'utils/multisite';

const DEFAULT_GROUP_FILTER_SIZE = 6;

type SupportsUserGroupFilterSearchParams =
  | UseSearchPartsParams
  | UseSearchQuotesParams
  | UseSearchQuoteItemsParams
  | UseSearchOrdersParams
  | UseSearchOrderItemsParams;

type UserGroupFilterProps = {
  agg: { buckets: { key: string }[] | null };
  selectedGroupIds: string[];
  setFilters: React.Dispatch<
    React.SetStateAction<SupportsUserGroupFilterSearchParams['filters']>
  >;
};

export const UserGroupFilter = ({
  agg,
  selectedGroupIds,
  setFilters,
}: UserGroupFilterProps) => {
  const isMultisiteEnabled = useFeatureFlagValue(
    featureFlagKeys.isMultisiteFunctionalityAvailable,
  );
  const userGroupCollectionId = useAppSelector(getUserGroupCollectionId);

  const [filterSize, setFilterSize] = useState(DEFAULT_GROUP_FILTER_SIZE);

  const userGroups = useVisibleUserGroups();

  const toggleUserGroup = useCallback(
    (groupId: string) => {
      setFilters((currentFilters) => {
        const currentGroupIds = currentFilters.groupIds?.values ?? [];
        const nextGroupIds = currentGroupIds.includes(groupId)
          ? currentGroupIds.filter((value) => value !== groupId)
          : [...currentGroupIds, groupId];

        return {
          ...currentFilters,
          groupIds: {
            ...currentFilters.groupIds,
            values: nextGroupIds,
          },
        };
      });
    },
    [setFilters],
  );

  const onSeeMore = useCallback(() => {
    setFilterSize(userGroups.length);
  }, [userGroups.length]);

  if (!agg?.buckets || agg.buckets.length === 0) {
    return null;
  }

  return isMultisiteEnabled && !!userGroupCollectionId ? (
    <Box mb={2}>
      <FormControl>
        <FormLabel>Site</FormLabel>
        <CheckboxGroup direction="column">
          {userGroups
            .toSorted((a, b) => a.businessName.localeCompare(b.businessName))
            .slice(0, filterSize)
            .map((userGroup) => {
              const isDisabled =
                !agg.buckets?.some(
                  (bucket) => Number(bucket.key) === userGroup.id,
                ) && !selectedGroupIds.includes(userGroup.id.toString());
              return (
                <Checkbox
                  isChecked={selectedGroupIds.includes(userGroup.id.toString())}
                  isDisabled={isDisabled}
                  onChange={() => toggleUserGroup(userGroup.id.toString())}
                  key={userGroup.id}
                  sx={{
                    '.chakra-checkbox__label': {
                      width: '100%',
                      minWidth: 0,
                    },
                  }}
                >
                  <UserGroupBadge
                    name={userGroup.businessName}
                    icon={userGroup.icon}
                    colorPalette={userGroup.color}
                    size="sm"
                    maxWidth="100%"
                    opacity={isDisabled ? 0.3 : 1}
                  />
                </Checkbox>
              );
            })}
        </CheckboxGroup>
        {userGroups.length > filterSize ? (
          <Button variant="link" size="sm" marginLeft={3} onClick={onSeeMore}>
            See more
          </Button>
        ) : null}
      </FormControl>
    </Box>
  ) : null;
};
